import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFormActions } from '../../../utils/form-helpers';
import rewardGiverInfoFormConfig from '../../../payment/reward-giver-info-form-config';
import { listOfWordsToCommaSeparatedText, pluralize } from '../../../utils';
import Input2 from '../../../components/input/input-2/Input2';
import styles from './reward-info-form.module.less';
import { determineCartContent, LOTTERY, NORMAL_REWARDS, PRODUCT_REWARDS } from '../cart/cart-model';
import PhoneNumberInput from '../../../components/input/PhoneNumberInput';
import isEmpty from 'lodash/isEmpty';
import { BodyText, Heading4 } from '@sb1/ffe-core-react';
import { ErrorFieldMessage } from '@sb1/ffe-form-react';

const { setFormField, setFormFields, validateForm, validateField } = getFormActions(rewardGiverInfoFormConfig);

const RewardInfoForm = ({ rewards }) => {
    const dispatch = useDispatch();
    const baseURL = useSelector((state) => state.baseURL);
    const rewardGiverInfoForm = useSelector((state) => state.rewardGiverInfoForm);
    const needsAddress = rewards?.some((r) => r.needsAddress);
    const needsPhonenumber = rewards?.some((r) => r.needsPhonenumber);
    const rewardTypes = useMemo(() => determineCartContent(rewards), [rewards]);
    const introText = listOfWordsToCommaSeparatedText([
        'navn',
        ...(needsAddress ? ['adresse'] : []),
        ...(needsPhonenumber ? ['telefonnummer'] : []),
    ]);

    useEffect(() => {
        dispatch(setFormField('needsPhonenumber', needsPhonenumber));
        dispatch(setFormField('needsAddress', needsAddress));
    }, [needsAddress, needsPhonenumber, dispatch]);

    const productAndLotteryText = useMemo(() => {
        const lotteryRewardType = rewardTypes?.find((r) => r.type === LOTTERY);
        const productRewardType = rewardTypes?.find((r) => r.type === PRODUCT_REWARDS);
        return lotteryRewardType || productRewardType
            ? `Oppgi ${introText} slik at Spleis kan sende deg ${
                  lotteryRewardType ? `${pluralize(lotteryRewardType.count, 'premien', 'premiene')} om du vinner.` : ''
              } ${
                  productRewardType
                      ? lotteryRewardType
                          ? ` ${pluralize(
                                productRewardType.count,
                                'Produktet',
                                'Produktene'
                            )} sendes også til samme adresse.`
                          : `${pluralize(productRewardType.count, 'produktet', 'produktene')}.`
                      : ''
              }`
            : null;
    }, [rewardTypes, introText]);
    const normalRewardsText = useMemo(() => {
        const normalRewardType = rewardTypes?.find((r) => r.type === NORMAL_REWARDS);
        return normalRewardType
            ? `Oppgi ${introText} slik at innsamler kan sende ${pluralize(
                  normalRewardType.count,
                  'belønningen',
                  'belønningene'
              )} til deg.`
            : null;
    }, [rewardTypes, introText]);

    const { errors } = rewardGiverInfoForm;

    const fetchPostalName = useCallback(
        async (e) => {
            const postalCode = e.target.value.trim();

            if (postalCode.length !== 4) {
                return dispatch(setFormFields({ postalCode, postalName: '' }));
            }

            const url = `${baseURL}/api/public/location/postalCode/${postalCode}/postalName`;
            const response = await fetch(url, {
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
            });
            const postalName = await response.text();
            return dispatch(setFormFields({ postalCode, postalName }, true));
        },
        [baseURL, dispatch]
    );

    return (
        <div className={styles.rewardInfoWrapper}>
            <Heading4 lookLike={5}>Leveringsinformasjon</Heading4>
            <form
                className={styles.form}
                onSubmit={(e) => {
                    e.preventDefault();
                    const formIsValid = isEmpty(dispatch(validateForm()));

                    if (formIsValid) {
                        dispatch(setFormField('hasEnteredAddress', true));
                    }
                }}
            >
                {productAndLotteryText && (
                    <BodyText tag={'h1'} className={styles.forminfo}>
                        {productAndLotteryText}{' '}
                        {rewardTypes.find((r) => r.type === NORMAL_REWARDS)
                            ? `Innsamler er ansvarlig for ${pluralize(
                                  rewardTypes.find((r) => r.type === NORMAL_REWARDS)?.count,
                                  'belønningen',
                                  'belønningene'
                              )}.`
                            : ''}
                    </BodyText>
                )}
                {normalRewardsText && !productAndLotteryText && (
                    <BodyText tag={'h1'} className={styles.forminfo}>
                        {normalRewardsText}
                    </BodyText>
                )}

                <Input2
                    title="Fornavn"
                    errors={errors}
                    fieldName="firstName"
                    autocomplete={'given-name'}
                    type="text"
                    onChange={(e) => dispatch(setFormField('firstName', e.target.value?.trim() || ''))}
                    onBlur={(e) => dispatch(setFormField('firstName', e.target.value.trim(), true))}
                    autoFocus
                />
                <Input2
                    title="Etternavn"
                    errors={errors}
                    fieldName="lastName"
                    autocomplete={'family-name'}
                    type="text"
                    onChange={(e) => dispatch(setFormField('lastName', e.target.value?.trim() || ''))}
                    onBlur={(e) => dispatch(setFormField('lastName', e.target.value.trim(), true))}
                />
                {needsPhonenumber && (
                    <PhoneNumberInput
                        errors={errors}
                        title="Telefonnummer"
                        fieldName="phoneNumber"
                        type="text"
                        onChange={(value) => dispatch(setFormField('phoneNumber', value.value))}
                        onBlur={() => dispatch(validateField('phoneNumber'))}
                    />
                )}
                {needsAddress && (
                    <>
                        <Input2
                            errors={errors}
                            title="Adresse"
                            fieldName="address"
                            type="text"
                            onChange={(e) => dispatch(setFormField('address', e.target.value.trim()))}
                            onBlur={(e) => dispatch(setFormField('address', e.target.value.trim(), true))}
                        />

                        <div>
                            <div className={styles.postalWrapper}>
                                <Input2
                                    fieldName="postalCode"
                                    type="tel"
                                    title={'Postnummer'}
                                    maxLength="4"
                                    onChange={fetchPostalName}
                                    onBlur={(e) => dispatch(setFormField('postalCode', e.target.value.trim(), true))}
                                    wrapperClass={styles.postalInput}
                                    extraMargin={false}
                                />
                                <span className={styles.postalPlace}>{rewardGiverInfoForm.postalName}</span>
                            </div>
                            {(errors?.postalCode || errors?.postalName) && (
                                <ErrorFieldMessage>{errors?.postalCode || errors?.postalName}</ErrorFieldMessage>
                            )}
                        </div>
                    </>
                )}
            </form>
        </div>
    );
};

export default RewardInfoForm;
